<script setup>
/**
 * Admin → Restaurant Data Review (RM-403, Kapil and the product advisor
 * 10/13): what the website reader found that a person should decide.
 *
 * One list. Each row is a restaurant, what is on file, what was found, where
 * it was found, and Accept or Dismiss. Links first, emails second (Kapil's
 * order of importance). Everything else (what was already decided, the first
 * run, the switches) sits under More, so the page opens on the next thing to
 * decide.
 *
 * The rules (what is applied on its own, what waits here) live on the server
 * in lib/websiteReader; this page draws what /admin/restaurant-data-review
 * returns.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import axios from '@axios'

const API = '/admin/restaurant-data-review'
const PAGE_SIZE = 25

const kind = ref('reservation_link')
const status = ref('open')
const search = ref('')
const items = ref([])
const total = ref(0)
const page = ref(1)
const loading = ref(false)
const loadingMore = ref(false)
const error = ref('')
const busy = ref({}) // row id → true while it saves
const rowError = ref({})
const decided = ref({}) // row id → 'Accepted' | 'Dismissed', shown in place until the list reloads
const summary = ref(null)
const showMore = ref(false)
const firstRunBusy = ref(false)
const firstRunNote = ref('')

const STATUS_OPTIONS = [
  { title: 'To review', value: 'open' },
  { title: 'Applied by RM', value: 'applied' },
  { title: 'Accepted', value: 'accepted' },
  { title: 'Dismissed', value: 'dismissed' },
]

const formatDate = d => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '')

// A web address as a person reads it: no scheme, no trailing slash.
const shortUrl = url => String(url || '').replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '')

// Only a web address becomes a link: what is on file was typed by a person and could be anything.
const isWebUrl = url => /^https?:\/\//i.test(String(url || ''))

const emailLists = [
  { key: 'general', label: 'Reservation Referrals' },
  { key: 'private', label: 'Private Event Requests' },
]

// Only the lists a finding would change are shown, on both sides.
const changedLists = item => emailLists.filter(l => {
  const d = item.discovered?.[l.key]

  return d && ((d.added || []).length || (d.removed || []).length)
})

const proposedEmails = (item, key) => (item.discovered?.[key]?.proposed || []).map(e => e.email)

const decidedLine = item => {
  const when = item.decidedAt ? ` · ${formatDate(item.decidedAt)}` : ''
  if (item.status === 'applied') return `Applied by RM${when}`
  const word = item.status === 'accepted' ? 'Accepted' : 'Dismissed'
  const who = item.decidedByName ? ` by ${item.decidedByName}` : ''

  return `${word}${who}${when}`
}

const statusLine = computed(() => {
  const s = summary.value
  if (!s || !s.read?.total) return ''
  const parts = [`${s.read.total} websites read`, `${s.links.applied} links added`]
  if (s.emails.applied) parts.push(`${s.emails.applied} email lists updated`)
  if (s.read.couldNotBeRead) parts.push(`${s.read.couldNotBeRead} could not be read`)
  if (s.queue.queued + s.queue.running) parts.push(`${s.queue.queued + s.queue.running} still queued`)

  return parts.join(' · ')
})

const switchLine = computed(() => {
  const w = summary.value?.switches
  if (!w) return ''

  return [
    `Reading websites: ${w.reading ? 'on' : 'off'}`,
    `Links: ${w.autoApplyLinks ? 'applied automatically when clear and verified' : 'review only'}`,
    `Emails: ${w.autoApplyEmails ? 'applied automatically when not verified and system-generated' : 'review only'}`,
  ].join(' · ')
})

const hasMore = computed(() => items.value.length < total.value)

async function loadSummary() {
  try {
    const { data } = await axios.get(`${API}/status`)

    summary.value = data?.data || null
  } catch { /* the list still works without its counts */ }
}

async function load({ more = false } = {}) {
  if (more) loadingMore.value = true
  else loading.value = true
  error.value = ''
  try {
    const nextPage = more ? page.value + 1 : 1

    const { data } = await axios.get(API, { params: { kind: kind.value, status: status.value, q: search.value.trim() || undefined, page: nextPage, limit: PAGE_SIZE } })

    const result = data?.data || { items: [], total: 0 }

    items.value = more ? [...items.value, ...result.items] : result.items
    total.value = result.total
    page.value = nextPage
    if (!more) decided.value = {}
  } catch (e) {
    error.value = e?.response?.data?.error || 'Could not load Restaurant Data Review.'
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

watch([kind, status], () => load())

let searchTimer = null
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => load(), 350)
})

// While websites are being read, the counts move: refresh them, quietly.
let poll = null
onMounted(() => {
  load()
  loadSummary()
  poll = setInterval(() => {
    const q = summary.value?.queue
    if (q && q.queued + q.running > 0) loadSummary()
  }, 20000)
})
onBeforeUnmount(() => { clearInterval(poll); clearTimeout(searchTimer) })

// One save at a time per row; the row shows its own result or error.
async function decide(item, action, body) {
  busy.value = { ...busy.value, [item.id]: true }
  rowError.value = { ...rowError.value, [item.id]: '' }
  try {
    await axios.post(`${API}/${item.id}/${action}`, body || {})
    decided.value = { ...decided.value, [item.id]: action === 'accept' ? 'Accepted' : 'Dismissed' }
    loadSummary()
  } catch (e) {
    rowError.value = { ...rowError.value, [item.id]: e?.response?.data?.error || 'Could not save that. Please try again.' }
  } finally {
    busy.value = { ...busy.value, [item.id]: false }
  }
}

const accept = (item, option) => decide(item, 'accept', option === undefined ? {} : { option })
const dismiss = item => decide(item, 'dismiss')

async function startFirstRun() {
  const n = summary.value?.restaurants?.withoutLinkWithWebsite || 0

  // It starts reading several hundred websites, so it is confirmed.
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Read the websites of ${n} restaurants that have no reservation link?`)) return
  firstRunBusy.value = true
  firstRunNote.value = ''
  try {
    const { data } = await axios.post(`${API}/first-run`, { scope: 'no_link' })

    firstRunNote.value = `${data?.data?.queued || 0} restaurants queued. Findings show here as each website is read.`
    loadSummary()
  } catch (e) {
    firstRunNote.value = e?.response?.data?.error || 'Could not start that. Please try again.'
  } finally {
    firstRunBusy.value = false
  }
}
</script>

<template>
  <div class="data-review">
    <header class="data-review__header">
      <h1 class="data-review__title">
        Restaurant Data Review
      </h1>
      <VBtnToggle
        v-model="kind"
        mandatory
        density="compact"
        variant="outlined"
        divided
      >
        <VBtn value="reservation_link">
          Links<template v-if="summary">
            &nbsp;{{ summary.toReview.links }}
          </template>
        </VBtn>
        <VBtn value="emails">
          Emails<template v-if="summary">
            &nbsp;{{ summary.toReview.emails }}
          </template>
        </VBtn>
      </VBtnToggle>
    </header>

    <p
      v-if="statusLine"
      class="data-review__status"
    >
      {{ statusLine }}
    </p>

    <p
      v-if="error"
      class="data-review__error"
    >
      {{ error }}
    </p>

    <div
      v-if="loading && !items.length"
      class="data-review__skeleton"
    >
      <div class="data-review__skeleton-line short" />
      <div class="data-review__skeleton-line" />
      <div class="data-review__skeleton-line" />
    </div>

    <p
      v-else-if="!items.length && !error"
      class="data-review__empty"
    >
      <template v-if="status !== 'open'">
        Nothing here yet.
      </template>
      <template v-else-if="summary && !summary.read.total">
        No websites have been read yet. Start the first run under More.
      </template>
      <template v-else>
        Nothing to review.
      </template>
    </p>

    <ul
      v-else
      class="data-review__list"
    >
      <li
        v-for="item in items"
        :key="item.id"
        class="data-review__row"
      >
        <p class="data-review__restaurant">
          <RouterLink
            v-if="item.profileId"
            :to="`/r/${item.profileId}`"
            class="data-review__name"
          >
            {{ item.restaurantName }}
          </RouterLink>
          <span
            v-else
            class="data-review__name"
          >{{ item.restaurantName }}</span>
          <span
            v-if="item.market"
            class="data-review__city"
          >{{ item.market }}</span>
        </p>
        <p
          v-if="item.reasonText"
          class="data-review__reason"
        >
          {{ item.reasonText }}
        </p>

        <dl class="data-review__facts">
          <!-- ── a reservation link ── -->
          <template v-if="item.kind === 'reservation_link'">
            <div class="data-review__fact">
              <dt>On file</dt>
              <dd>
                <a
                  v-if="isWebUrl(item.existing?.url)"
                  :href="item.existing.url"
                  target="_blank"
                  rel="noopener noreferrer"
                >{{ shortUrl(item.existing.url) }}</a>
                <span v-else-if="item.existing?.url">{{ item.existing.url }}</span>
                <span
                  v-else
                  class="data-review__none"
                >No link</span>
              </dd>
            </div>
            <div class="data-review__fact">
              <dt>Found</dt>
              <dd>
                <div
                  v-for="(option, i) in item.discovered?.options || []"
                  :key="option.url"
                  class="data-review__option"
                >
                  <div class="data-review__option-text">
                    <a
                      :href="option.url"
                      target="_blank"
                      rel="noopener noreferrer"
                    >{{ shortUrl(option.url) }}</a>
                    <span class="data-review__note">{{ [option.platformName, option.note].filter(Boolean).join(' · ') }}</span>
                  </div>
                  <VBtn
                    v-if="item.status === 'open' && !decided[item.id]"
                    color="primary"
                    variant="flat"
                    class="data-review__accept"
                    :loading="busy[item.id]"
                    :disabled="busy[item.id]"
                    @click="accept(item, i)"
                  >
                    Accept
                  </VBtn>
                </div>
              </dd>
            </div>
          </template>

          <!-- ── email addresses ── -->
          <template v-else>
            <div class="data-review__fact">
              <dt>On file</dt>
              <dd>
                <p
                  v-for="l in changedLists(item)"
                  :key="l.key"
                  class="data-review__emails"
                >
                  <span class="data-review__list-label">{{ l.label }}</span>
                  <span v-if="(item.existing?.[l.key] || []).length">{{ item.existing[l.key].join(', ') }}</span>
                  <span
                    v-else
                    class="data-review__none"
                  >None</span>
                </p>
              </dd>
            </div>
            <div class="data-review__fact">
              <dt>Found</dt>
              <dd>
                <p
                  v-for="l in changedLists(item)"
                  :key="l.key"
                  class="data-review__emails"
                >
                  <span class="data-review__list-label">{{ l.label }}</span>
                  <span>{{ proposedEmails(item, l.key).join(', ') }}</span>
                </p>
              </dd>
            </div>
          </template>

          <div class="data-review__fact">
            <dt>Source</dt>
            <dd>
              <span
                v-if="item.source?.kind === 'openai'"
                class="data-review__source"
              >OpenAI web research</span>
              <a
                v-for="p in item.source?.pages || []"
                :key="p"
                :href="p"
                target="_blank"
                rel="noopener noreferrer"
                class="data-review__source"
              >{{ shortUrl(p) }}</a>
              <span
                v-if="item.source?.kind !== 'openai' && !(item.source?.pages || []).length"
                class="data-review__none"
              >The restaurant's website</span>
            </dd>
          </div>
        </dl>

        <p
          v-if="decided[item.id] || item.status !== 'open'"
          class="data-review__decided"
        >
          {{ decided[item.id] || decidedLine(item) }}
        </p>
        <div
          v-else
          class="data-review__actions"
        >
          <VBtn
            v-if="item.kind === 'emails'"
            color="primary"
            variant="flat"
            class="data-review__accept"
            :loading="busy[item.id]"
            :disabled="busy[item.id]"
            @click="accept(item)"
          >
            Accept
          </VBtn>
          <VBtn
            variant="text"
            class="data-review__dismiss"
            :disabled="busy[item.id]"
            @click="dismiss(item)"
          >
            Dismiss
          </VBtn>
        </div>

        <p
          v-if="rowError[item.id]"
          class="data-review__error"
        >
          {{ rowError[item.id] }}
        </p>
      </li>
    </ul>

    <VBtn
      v-if="hasMore"
      variant="text"
      block
      class="data-review__more-rows"
      :loading="loadingMore"
      @click="load({ more: true })"
    >
      Show more ({{ total - items.length }})
    </VBtn>

    <section class="data-review__more">
      <VBtn
        variant="text"
        class="data-review__more-toggle"
        :aria-expanded="showMore"
        @click="showMore = !showMore"
      >
        {{ showMore ? 'Less' : 'More' }}
      </VBtn>

      <div v-if="showMore">
        <VSelect
          v-model="status"
          :items="STATUS_OPTIONS"
          label="Show"
          density="comfortable"
          hide-details
          class="data-review__field"
        />
        <VTextField
          v-model="search"
          label="Restaurant name"
          density="comfortable"
          hide-details
          clearable
          autocomplete="off"
          class="data-review__field"
        />

        <template v-if="summary">
          <p class="data-review__more-line">
            {{ summary.restaurants.withoutLinkWithWebsite }} restaurants have a website and no reservation link.
          </p>
          <VBtn
            variant="outlined"
            :loading="firstRunBusy"
            :disabled="firstRunBusy || !summary.switches.reading || !summary.restaurants.withoutLinkWithWebsite"
            @click="startFirstRun"
          >
            Read their websites
          </VBtn>
          <p
            v-if="firstRunNote"
            class="data-review__more-line"
          >
            {{ firstRunNote }}
          </p>
          <p class="data-review__more-line data-review__switches">
            {{ switchLine }}
          </p>
        </template>
      </div>
    </section>
  </div>
</template>

<style scoped>
.data-review {
  max-width: 720px;
  margin: 0 auto;
  padding: 16px;
}

.data-review__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.data-review__title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--ds-text-strong, rgb(var(--v-theme-on-surface)));
}

.data-review__status {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.data-review__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

/* One finding: the restaurant, why it is here, on file, found, source, then the decision. */
.data-review__row {
  padding: 16px 0;
  border-top: 1px solid var(--ds-border-subtle, rgba(var(--v-theme-on-surface), 0.08));
}

.data-review__restaurant {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 8px;
  margin: 0;
}

.data-review__name {
  font-size: 16px;
  font-weight: 600;
  color: var(--ds-text-strong, rgb(var(--v-theme-on-surface)));
  text-decoration: none;
}

.data-review__city,
.data-review__reason,
.data-review__note {
  font-size: 14px;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.data-review__reason {
  margin: 4px 0 0;
  line-height: 1.5;
}

.data-review__facts {
  margin: 12px 0 0;
}

/* Label over value on a phone; label beside value from 600px. */
.data-review__fact {
  margin-top: 8px;
}

.data-review__fact dt {
  font-size: 14px;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.data-review__fact dd {
  margin: 2px 0 0;
  font-size: 16px;
  line-height: 1.5;
  color: var(--ds-text-body, rgb(var(--v-theme-on-surface)));
  /* A booking link is one long word: let it wrap instead of pushing the page sideways. */
  overflow-wrap: anywhere;
}

.data-review__fact a {
  color: var(--ds-brand-primary-dark, rgb(var(--v-theme-primary)));
  text-decoration: none;
}

.data-review__none {
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.data-review__option {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.data-review__option + .data-review__option {
  margin-top: 12px;
}

.data-review__option-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.data-review__emails {
  display: flex;
  flex-direction: column;
  margin: 0 0 4px;
}

.data-review__list-label {
  font-size: 14px;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.data-review__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

/* 44px targets: the decision is made with a thumb. */
.data-review__accept,
.data-review__dismiss,
.data-review__more-toggle {
  min-height: 44px;
}

.data-review__accept {
  flex-shrink: 0;
}

/* The accent is Accept's alone; Dismiss and More stay neutral (the app's button default is the brand color). */
.data-review__dismiss,
.data-review__more-toggle {
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6)) !important;
}

/* One source per line. */
.data-review__source {
  display: block;
}

.data-review__decided {
  margin: 12px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--ds-text-body, rgb(var(--v-theme-on-surface)));
}

.data-review__more-rows {
  margin-top: 8px;
}

.data-review__more {
  margin-top: 24px;
  padding-top: 8px;
  border-top: 1px solid var(--ds-border-subtle, rgba(var(--v-theme-on-surface), 0.08));
}

.data-review__more-toggle {
  margin-left: -16px;
}

.data-review__field {
  margin-top: 12px;
}

/* iOS zooms the page on focus when an input is under 16px. */
.data-review__field :deep(input),
.data-review__field :deep(.v-field__input) {
  font-size: 16px;
}

.data-review__more-line {
  margin: 16px 0 8px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ds-text-body, rgb(var(--v-theme-on-surface)));
}

.data-review__switches {
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.data-review__empty {
  font-size: 16px;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.data-review__error {
  margin: 8px 0 0;
  font-size: 14px;
  color: rgb(var(--v-theme-error));
}

.data-review__skeleton-line {
  height: 14px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.08);
}

.data-review__skeleton-line.short {
  width: 60%;
}

@media (min-width: 600px) {
  .data-review__fact {
    display: grid;
    grid-template-columns: 96px 1fr;
    gap: 16px;
    align-items: baseline;
  }

  .data-review__fact dd {
    margin: 0;
  }
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  requiresAdmin: true
</route>
